// Shrinking on this device (WebCodecs, through mediabunny). Whatever this device can't do throws NoDevice — then THE WALL's
// server does it instead. The shrunk copy is written to the browser's private file storage when it can (keeps memory low).
export class NoDevice extends Error {}

export const MB = 1024 * 1024;
const KBPS = { 1080: 8000, 720: 4500 };
const even = (n) => Math.max(2, Math.round(n / 2) * 2);
const base = (name) => name.replace(/\.[^.]+$/, "");
const lib = () => import("mediabunny");

// Video bitrate that keeps the whole file under `limit` bytes (never above the size's normal rate).
export const fitKbps = (size, limit, duration, audioKbps) => Math.min(KBPS[size], Math.floor((limit * 0.92 * 8) / Math.max(1, duration) / 1000) - audioKbps);
export const tooLong = (limit) => new Error(`This video is too long to fit in ${Math.round(limit / MB)} MB even when shrunk — cut it into shorter parts`);

export async function inspect(file) {
  const M = await lib();
  const input = new M.Input({ formats: M.ALL_FORMATS, source: new M.BlobSource(file) });
  try {
    const v = await input.getPrimaryVideoTrack(), a = await input.getPrimaryAudioTrack();
    const duration = (await input.getDurationFromMetadata().catch(() => null)) || (await input.computeDuration());
    const fps = v ? (await v.computePacketStats(120).catch(() => null))?.averagePacketRate || 30 : 0;
    return { width: v?.displayWidth || 0, height: v?.displayHeight || 0, duration, fps, vcodec: v?.codec || null, acodec: a?.codec || null };
  } finally { input.dispose(); }
}

async function sink(M, name) {
  try {
    const root = await navigator.storage.getDirectory();
    const fh = await root.getFileHandle(name, { create: true });
    const w = await fh.createWritable();
    return { target: new M.StreamTarget(w, { chunked: true, chunkSize: 8 * MB }), done: () => fh.getFile(), drop: () => root.removeEntry(name).catch(() => {}) };
  } catch {
    const t = new M.BufferTarget();
    return { target: t, done: async () => new Blob([t.buffer]), drop: () => {} };
  }
}

const problem = (conv, keep = () => true) => {
  const bad = conv.discardedTracks.filter((d) => d.reason !== "discarded_by_user" && keep(d));
  return !conv.isValid || bad.length ? new NoDevice(`This device can't do it (${bad.map((d) => d.reason).join(", ") || "not supported"})`) : null;
};

export async function shrinkOnDevice(file, info, { size, limit, onProgress }) {
  if (typeof window.VideoEncoder === "undefined" || !info?.width) throw new NoDevice("This browser can't shrink videos");
  const M = await lib();
  const s = even(Math.min(info.width, info.height, size)), land = info.width >= info.height;
  const width = land ? even((info.width * s) / info.height) : s, height = land ? s : even((info.height * s) / info.width);
  const copyAudio = !info.acodec || info.acodec === "aac";
  const kbps = fitKbps(size, limit, info.duration, copyAudio ? 320 : 192);
  if (kbps < 500) throw tooLong(limit);
  if (!(await M.canEncodeVideo("avc", { width, height, bitrate: kbps * 1000 }))) throw new NoDevice("This device can't make H.264 video");
  const name = `shrink_${Date.now()}.mp4`;
  const out = await sink(M, name);
  const input = new M.Input({ formats: M.ALL_FORMATS, source: new M.BlobSource(file) });
  try {
    const output = new M.Output({ format: new M.Mp4OutputFormat({ fastStart: false }), target: out.target });
    const conv = await M.Conversion.init({ input, output, tracks: "primary", showWarnings: false,
      video: { width, height, fit: "fill", codec: "avc", bitrate: kbps * 1000, frameRate: info.fps > 31 ? 30 : undefined, forceTranscode: true },
      audio: copyAudio ? {} : { codec: "aac", bitrate: 192000 } });
    const bad = problem(conv);
    if (bad) throw bad;
    conv.onProgress = (p) => onProgress?.(p);
    await conv.execute();
    const blob = await out.done();
    return { file: new File([blob], `${base(file.name)}-${s}p.mp4`, { type: "video/mp4" }), drop: out.drop };
  } catch (e) {
    out.drop();
    throw e instanceof NoDevice ? e : new NoDevice(e?.message || "Shrinking stopped on this device");
  } finally { input.dispose(); }
}

// Just the sound: AAC / FLAC / PCM copied as they are (no quality lost), anything else made into AAC 256k.
export async function soundOnDevice(file, info, { onProgress }) {
  if (!info.acodec) throw new Error("That video has no sound to send");
  const M = await lib();
  const kind = info.acodec === "aac" ? "m4a" : /^pcm/.test(info.acodec) ? "wav" : info.acodec === "flac" ? "flac" : "aac";
  const format = kind === "wav" ? new M.WavOutputFormat() : kind === "flac" ? new M.FlacOutputFormat() : new M.Mp4OutputFormat();
  const target = new M.BufferTarget();
  const input = new M.Input({ formats: M.ALL_FORMATS, source: new M.BlobSource(file) });
  try {
    const conv = await M.Conversion.init({ input, output: new M.Output({ format, target }), tracks: "primary", showWarnings: false,
      video: { discard: true }, audio: kind === "aac" ? { codec: "aac", bitrate: 256000 } : {} });
    const bad = problem(conv, (d) => d.track.type === "audio");
    if (bad) throw bad;
    conv.onProgress = (p) => onProgress?.(p);
    await conv.execute();
    const ext = kind === "aac" ? "m4a" : kind;
    return new File([target.buffer], `${base(file.name)} - sound.${ext}`, { type: ext === "wav" ? "audio/wav" : ext === "flac" ? "audio/flac" : "audio/mp4" });
  } catch (e) {
    throw e instanceof NoDevice ? e : new NoDevice(e?.message || "Couldn't take the sound out on this device");
  } finally { input.dispose(); }
}
